import { useCallback } from 'react';
import { toast } from '@/hooks/shared/use-toast';
import type { VerticalLine } from '@/modules/shared/DataLayers/types';

export interface UseVerticalLineMenuHandlersProps {
  selectedVerticalLineId: string | null;
  clickedChartId: string | null;
  verticalLines: VerticalLine[];
  verticalLineMenuPosition: { x: number; y: number } | null;
  data: Record<string, any>;
  sectionConfigGraphs: any[];
  replaceMarkersOnHarmonicCreation: boolean;
  setSelectedVerticalLineId: React.Dispatch<React.SetStateAction<string | null>>;
  setVerticalLineMenuPosition: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;
  setBenchmarkLineBySection: React.Dispatch<React.SetStateAction<Record<string, string | null>>>;
  setEditingHarmonicGroup: React.Dispatch<React.SetStateAction<any | null>>;
  setPendingHarmonicCreation: React.Dispatch<React.SetStateAction<any | null>>;
  removeVerticalLine: (lineId: string) => void;
  getHarmonicGroupCount: (sectionKey: string) => number;
  getAvailableHarmonicPrefixes: (sectionKey: string) => string[];
  getSideBandsGroupCount: (sectionKey: string) => number;
  getHarmonicInterval: (sectionKey: string, xAxisType: 'date' | 'numeric' | 'category') => any;
}

export interface UseVerticalLineMenuHandlersReturn {
  handleSetAsBenchmark: () => void;
  handleSetAsHarmonics: () => void;
  handleSetAsSideBands: () => void;
  handleRemove: () => void;
}

/**
 * Handles VerticalLineMenu actions (Set as Benchmark, Set as Harmonics, Set as Side Bands, Remove)
 * Manages marker line operations for creating harmonics, side bands, or setting reference lines
 */
export function useVerticalLineMenuHandlers({
  selectedVerticalLineId,
  clickedChartId,
  verticalLines,
  verticalLineMenuPosition,
  data,
  sectionConfigGraphs,
  replaceMarkersOnHarmonicCreation,
  setSelectedVerticalLineId,
  setVerticalLineMenuPosition,
  setBenchmarkLineBySection,
  setEditingHarmonicGroup,
  setPendingHarmonicCreation,
  removeVerticalLine,
  getHarmonicGroupCount,
  getAvailableHarmonicPrefixes,
  getSideBandsGroupCount,
  getHarmonicInterval,
}: UseVerticalLineMenuHandlersProps): UseVerticalLineMenuHandlersReturn {

  const handleSetAsBenchmark = useCallback(() => {
    if (selectedVerticalLineId) {
      // Find the clicked line
      const clickedLine = verticalLines.find((line: VerticalLine) => line.id === selectedVerticalLineId);

      if (clickedLine && selectedVerticalLineId) {
        // Set this line as the benchmark for ALL sections
        // Markers are global (no sectionKey), so we always set for all sections
        const lineId = selectedVerticalLineId;

        setBenchmarkLineBySection((prev) => {
          const allSectionKeys = sectionConfigGraphs.map((g: any) => g.id);
          const newState = { ...prev };
          // Set the same benchmark for all sections
          allSectionKeys.forEach((key: string) => {
            newState[key] = lineId;
          });
          return newState;
        });
      }
    }

    // Close the menu
    setSelectedVerticalLineId(null);
    setVerticalLineMenuPosition(null);
  }, [
    selectedVerticalLineId,
    verticalLines,
    sectionConfigGraphs,
    setSelectedVerticalLineId,
    setVerticalLineMenuPosition,
    setBenchmarkLineBySection,
  ]);

  const handleSetAsHarmonics = useCallback(() => {
    if (selectedVerticalLineId) {
      // Find the clicked line
      const clickedLine = verticalLines.find((line: VerticalLine) => line.id === selectedVerticalLineId);
      // Use clickedChartId state (for markers) or the line's own chartId (for existing harmonics)
      const effectiveChartId = clickedChartId || clickedLine?.chartId;
      if (clickedLine && effectiveChartId) {
        // Get the sectionKey from effectiveChartId (remove -combined or -split suffix)
        const sectionKey = clickedLine.sectionKey || effectiveChartId.replace(/-combined$|-split$/, '');

        // Get current HARMONIC group count (H, I, J only)
        const currentHarmonicCount = getHarmonicGroupCount(sectionKey);

        if (currentHarmonicCount >= 3) {
          toast({
            title: 'Maximum harmonic groups reached!',
            description: `You can only add up to 3 harmonic groups (H, I, J) per section. Current harmonic groups: ${currentHarmonicCount}/3. To add more, please remove an existing harmonic group first.`,
          });
          // Close the menu
          setSelectedVerticalLineId(null);
          setVerticalLineMenuPosition(null);
          return;
        }

        // Get FIRST available harmonic prefix
        const availableHarmonicPrefixes = getAvailableHarmonicPrefixes(sectionKey);
        const labelPrefix = availableHarmonicPrefixes[0];

        // Set group colors: H = color 0, I = color 1, J = color 2
        const groupColors = [
          '#55687D', // H: Gray-blue
          '#7B8A9D', // I: Lighter gray-blue
          '#9BA8B7', // J: Even lighter
        ];
        const colorIndex = ['H', 'I', 'J'].indexOf(labelPrefix);
        const groupColor = groupColors[colorIndex] || '#55687D';

        // Default settings
        const multiplier = 6;
        const startingPoint =
          clickedLine.timestamp instanceof Date ? clickedLine.timestamp.getTime() : clickedLine.timestamp;

        // Detect x-axis type from the section data
        const sectionData = data[sectionKey];
        let xAxisType: 'date' | 'numeric' = 'date';
        if (sectionData?.data?.[0]?.x && sectionData.data[0].x.length > 0) {
          const firstX = sectionData.data[0].x[0];
          xAxisType = typeof firstX === 'number' ? 'numeric' : 'date';
        }

        // Get the configured interval for this section (default: 'days' for date, 1 for numeric)
        const intervalType = getHarmonicInterval(sectionKey, xAxisType);

        // Open the HarmonicEditDialog to let user configure before creating
        setEditingHarmonicGroup({
          lineId: selectedVerticalLineId, // Store the marker line ID
          sectionKey,
          startingPoint,
          labelPrefix,
          multiplier,
          color: groupColor,
          chartId: effectiveChartId,
          xAxisType: xAxisType as 'date' | 'numeric' | 'category',
          intervalType: typeof intervalType === 'string' ? intervalType : 'days',
          position: verticalLineMenuPosition || undefined,
          isNewGroup: true, // Flag to indicate we're creating a new group, not editing
        } as any);
      }
    }

    // Close the menu (dialog will open)
    setSelectedVerticalLineId(null);
    setVerticalLineMenuPosition(null);
  }, [
    selectedVerticalLineId,
    clickedChartId,
    verticalLines,
    verticalLineMenuPosition,
    data,
    setSelectedVerticalLineId,
    setVerticalLineMenuPosition,
    setEditingHarmonicGroup,
    getHarmonicGroupCount,
    getAvailableHarmonicPrefixes,
    getHarmonicInterval,
  ]);

  const handleSetAsSideBands = useCallback(() => {
    if (selectedVerticalLineId) {
      // Find the clicked line
      const clickedLine = verticalLines.find((line: VerticalLine) => line.id === selectedVerticalLineId);
      // Use clickedChartId state (for markers) or the line's own chartId (for existing harmonics)
      const effectiveChartId = clickedChartId || clickedLine?.chartId;
      if (clickedLine && effectiveChartId) {
        // Get the sectionKey from effectiveChartId (remove -combined or -split suffix)
        const sectionKey = clickedLine.sectionKey || effectiveChartId.replace(/-combined$|-split$/, '');

        // Get current CARRIER group count (C, D, E only)
        const currentSideBandsCount = getSideBandsGroupCount(sectionKey);

        if (currentSideBandsCount >= 3) {
          toast({
            title: 'Maximum side bands groups reached!',
            description: `You can only add up to 3 carrier groups (C, D, E) per section. Current carrier groups: ${currentSideBandsCount}/3. To add more, please remove an existing carrier group first.`,
          });
          // Close the menu
          setSelectedVerticalLineId(null);
          setVerticalLineMenuPosition(null);
          return;
        }

        // Use FIRST available carrier prefix (C first, then D, then E)
        const defaultLabelPrefixes = ['C', 'D', 'E'];
        const labelPrefix = defaultLabelPrefixes[currentSideBandsCount];

        // Set group colors: C = color 0, D = color 1, E = color 2
        const groupColors = [
          '#55687D', // C: Gray-blue
          '#7B8A9D', // D: Lighter gray-blue
          '#9BA8B7', // E: Even lighter
        ];
        const colorIndex = ['C', 'D', 'E'].indexOf(labelPrefix);
        const groupColor = groupColors[colorIndex] || '#55687D';

        // Generate unique group ID for this side bands group
        const groupId = `group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        const centerPoint =
          clickedLine.timestamp instanceof Date ? clickedLine.timestamp.getTime() : clickedLine.timestamp;

        // Detect x-axis type from the section data
        const sectionData = data[sectionKey];
        let xAxisType: 'date' | 'numeric' = 'date';
        if (sectionData?.data?.[0]?.x && sectionData.data[0].x.length > 0) {
          const firstX = sectionData.data[0].x[0];
          xAxisType = typeof firstX === 'number' ? 'numeric' : 'date';
        }

        // If replaceMarkersOnHarmonicCreation is true, remove all markers for this section
        if (replaceMarkersOnHarmonicCreation) {
          const markersToRemove = verticalLines.filter(
            (line: VerticalLine) => !line.isHarmonic && line.sectionKey === sectionKey
          );
          markersToRemove.forEach((line: VerticalLine) => {
            removeVerticalLine(line.id);
          });
        }

        // Set pending state - waiting for second click to define interval
        setPendingHarmonicCreation({
          centerPoint,
          chartId: effectiveChartId,
          sectionKey,
          type: 'sideBands',
          labelPrefix,
          color: groupColor,
          xAxisType: xAxisType as 'date' | 'numeric' | 'category',
          groupId,
        });

        toast({
          title: 'Side Bands Mode Active',
          description: 'Click on the chart to set the interval between side bands lines',
        });
      }
    }

    // Close the menu
    setSelectedVerticalLineId(null);
    setVerticalLineMenuPosition(null);
  }, [
    selectedVerticalLineId,
    clickedChartId,
    verticalLines,
    data,
    replaceMarkersOnHarmonicCreation,
    setSelectedVerticalLineId,
    setVerticalLineMenuPosition,
    setPendingHarmonicCreation,
    removeVerticalLine,
    getSideBandsGroupCount,
  ]);

  const handleRemove = useCallback(() => {
    if (selectedVerticalLineId) {
      removeVerticalLine(selectedVerticalLineId);
    }

    // Close the menu
    setSelectedVerticalLineId(null);
    setVerticalLineMenuPosition(null);
  }, [selectedVerticalLineId, setSelectedVerticalLineId, setVerticalLineMenuPosition, removeVerticalLine]);

  return {
    handleSetAsBenchmark,
    handleSetAsHarmonics,
    handleSetAsSideBands,
    handleRemove,
  };
}
